import { DataTableSkeleton } from '@/components/data-table-skeleton';
import { DataTable } from '@/components/ui/data-table';
import { Label } from '@/features/label/components/label';
import { LabelGroup } from '@/features/label/types';
import { useAllOrganizations } from '@/features/organizer/api/get-all-organizations';
import { useAllPayments } from '@/features/payment/api/get-all-payments';
import { paymentColumns } from '@/features/payment/components/payment-columns';
import { OrderRow, PayType } from '@/features/payment/types';
import { getKeyByValue } from '@/utils/get-key-by-value';
import { label } from '@/utils/label';
import { Row } from '@tanstack/react-table';
import { PaymentFilters } from '../components/(filters)/payment-filters';
import { useState } from 'react';
import { useYear } from '@/features/settings/api/get-year';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Filter } from 'lucide-react';

export function Payments() {
  const [filterByYear, setFilterByYear] = useState<number | null>(null);

  useYear({
    config: {
      onSuccess: (data: number) => {
        setFilterByYear(data);
      }
    }
  });

  const { data: payments = [], isLoading } = useAllPayments({});
  const { data: organizations = [] } = useAllOrganizations({});

  const extendedPayments = payments?.map(payment => ({
    ...payment,
    organizerName: organizations.find(org => org.organizerId === payment.payment?.organizerId)?.organizerName ?? '',
    orgNo: organizations.find(org => org.organizerId === payment.payment?.organizerId)?.orgNo ?? '',
    invoiceNo: organizations.find(org => org.organizerId === payment.payment?.organizerId)?.invoiceOrgNo ?? '',
    productType: payment.productType ?? 0
  }));

  const availableYears = extendedPayments
    .reduce((acc: number[], p) => (acc.includes(p.payment?.yearId) ? acc : [...acc, p.payment?.yearId]), [])
    .sort((a, b) => b - a);

  const filteredPayments = filterByYear
    ? extendedPayments.filter(p => p.payment?.yearId === filterByYear)
    : extendedPayments;

  return (
    <div className="sm:space-y-6 flex flex-col h-full">
      <div className="flex w-full items-center justify-between">
        <div>
          <div className="flex gap-x-1 items-center">
            <h3 className="sm:text-xl font-semibold">
              <Label name="page_title" groupName={LabelGroup.ADMIN_PAYMENT} />
            </h3>
          </div>
        </div>
        {availableYears.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-1" />
                År {filterByYear}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-30">
              <DropdownMenuLabel>
                <Label name="Year" groupName={LabelGroup.ORGANIZER_EVENT_LIST} />
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableYears.map(year => (
                <DropdownMenuCheckboxItem key={year} className="flex items-center gap-x-2" checked={filterByYear === year} onCheckedChange={() => setFilterByYear(year)}>
                  {year}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="space-y-2">
        {!isLoading ? (
          <DataTable
            columns={paymentColumns}
            data={filteredPayments}
            columnVisibility={{
              productType: false
            }}
            FacetedFilters={PaymentFilters}
            config={{
              maxHeight: 'calc(100lvh - 260px)',
              getSearchFields: row => [
                (row as Row<OrderRow>).original?.name.toString(),
                (row as Row<OrderRow>).original.payment?.payDate.toString(),
                (row as Row<OrderRow>).original.payment?.paymentId.toString(),
                (
                  row as Row<
                    OrderRow & {
                      invoiceNo: string;
                    }
                  >
                ).original?.invoiceNo.toString(),
                (
                  row as Row<
                    OrderRow & {
                      orgNo: string;
                    }
                  >
                ).original?.orgNo.toString(),
                (row as Row<OrderRow>).original.payment?.organizerId.toString(),
                (row as Row<any>).original.payment?.organizerName?.toLowerCase()?.toString(),
                label(getKeyByValue(PayType, (row as Row<OrderRow>).original.payment?.payType) ?? '', LabelGroup.ADMIN_PAYMENT),
                (row as Row<OrderRow>).original?.amount.toString()
              ]
            }}
          />
        ) : (
          <DataTableSkeleton />
        )}
      </div>
    </div>
  );
}
